import { VStack } from '@chakra-ui/react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Show } from 'shared';
import { Flex } from 'shared_DEPRECATED/components/Flex';
import { HotjarSuppression } from 'shared_DEPRECATED/components/Hotjar';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import Spacer from 'shared_DEPRECATED/components/Spacer';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';

import {
  SessionsFormCoachingToolsLink,
  SessionsFormCoachingToolsMiroBoardsList,
  SessionsFormCoachingToolsSection,
} from 'features/sessions/components/Form/CoachingTools';
import { SessionsFormCoachingToolsCompletionIndicator } from 'features/sessions/components/Form/CoachingTools/CompletionIndicator';
import { MIRO_BOARDS_FIELD_KEY } from 'features/sessions/config';
import { CoachingToolNote } from 'features/sessions/config/types';
import { isHosCoachingTool } from 'features/sessions/utils';

import { Badge } from 'shared/components/Badge';
import { ButtonIcon } from 'shared/components/Button';
import { Typography } from 'shared/components/Typography';

type SessionsFormCoachingToolsCardViewProps = {
  coachingTool: CoachingToolNote;
  onDelete: () => void;
  formKey: string;
};

export const SessionsFormCoachingToolsCardView = ({
  coachingTool,
  onDelete,
  formKey,
}: SessionsFormCoachingToolsCardViewProps) => {
  const { setValue } = useFormContext();
  const { tool, toolEffectivenessRate, notes, link, unlistedToolName } =
    coachingTool;

  const miroBoardsComposedFieldKey = `${formKey}.${MIRO_BOARDS_FIELD_KEY}`;

  const miroBoards: Attachment[] =
    useWatch({ name: miroBoardsComposedFieldKey }) || [];

  return (
    <>
      <SessionsFormCoachingToolsSection>
        <Flex>
          <Typography type="smallHeading" fontWeight="semibold" color="black">
            {tool.label}
          </Typography>
          <Flex gap="var(--spacing04)">
            <ButtonIcon
              aria-label="edit tool"
              onClick={() => setValue(`${formKey}.editMode`, true)}
            >
              <Icon name={iconNames.pen} />
            </ButtonIcon>
            <ButtonIcon aria-label="delete tool" onClick={onDelete}>
              <Icon name={iconNames.bin} />
            </ButtonIcon>
          </Flex>
        </Flex>
      </SessionsFormCoachingToolsSection>
      <SessionsFormCoachingToolsSection>
        <Flex gap="var(--spacing04)" justifyContent="flex-start">
          <Typography type="meta" color="black">
            HDE perceived rate of effectiveness
          </Typography>
          <Badge colorPalette="green">
            <Typography type="small" color="white">
              {toolEffectivenessRate}
            </Typography>
          </Badge>
        </Flex>
      </SessionsFormCoachingToolsSection>
      {unlistedToolName && (
        <SessionsFormCoachingToolsSection>
          <Typography type="meta" fontWeight="semibold" color="black">
            External session tool name
          </Typography>
          <Spacer size="md zr" />
          <Typography type="meta" color="black">
            {unlistedToolName}
          </Typography>
        </SessionsFormCoachingToolsSection>
      )}
      {link && <SessionsFormCoachingToolsLink link={link} />}
      <Show when={isHosCoachingTool(coachingTool.tool)}>
        <SessionsFormCoachingToolsSection>
          <Spacer size="md zr" />
          <VStack gap={2} alignItems="flex-start">
            <Typography type="meta" fontWeight="semibold" color="black">
              Tool completion status
            </Typography>
            <SessionsFormCoachingToolsCompletionIndicator
              isCompleted={coachingTool.isCompleted}
            />
          </VStack>
        </SessionsFormCoachingToolsSection>
      </Show>
      <Show when={!!notes}>
        <SessionsFormCoachingToolsSection>
          <Typography type="meta" fontWeight="semibold" color="black">
            Session specific notes
          </Typography>
          <Spacer size="md zr" />
          <HotjarSuppression>
            <Typography type="meta" fontWeight="regular" color="black">
              {notes}
            </Typography>
          </HotjarSuppression>
        </SessionsFormCoachingToolsSection>
      </Show>
      <Show when={miroBoards.length}>
        <SessionsFormCoachingToolsSection>
          <Typography type="meta" fontWeight="semibold" color="black">
            Miro board pdf
          </Typography>
          <SessionsFormCoachingToolsMiroBoardsList
            formKey={miroBoardsComposedFieldKey}
          />
        </SessionsFormCoachingToolsSection>
      </Show>
    </>
  );
};
